import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

import { IdParamsType } from 'utils/types';
import { CustomFieldFormProps } from 'features/CustomFields/types';
import { CustomFieldType } from 'utils/types';
import { defaultValues, schema, createRequestBody, editFormValues } from 'features/CustomFields/utils';
import { deleteData, getData, post, put } from 'services/api';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import {
	useAccordionForm,
	useSeededEditForm,
} from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import { queryClient } from 'index';

import { CustomFieldsForm } from 'features/CustomFields/CustomFields.styles';
import { QuestionForm } from 'features/CustomFields/components/QuestionForm';
import { RightForm } from 'features/CustomFields/components/RightForm/RightForm';
import styles from './CustomFieldForm.module.css';

interface CustomFieldEditorProps {
	/** null → create mode; string → edit mode for this custom field ID */
	customFieldId: string | null;
	onDone: () => void;
}

/**
 * CustomFieldForm
 *
 * Renders the custom field create/edit form inline inside PunchupAccordionSection.
 * Replicates the logic from CustomFields + useCustomFieldsApi without router coupling:
 * accepts an explicit customFieldId and calls onDone() instead of history.goBack().
 */
export const CustomFieldForm: FC<CustomFieldEditorProps> = ({ customFieldId, onDone }) => {
	const { t } = useTranslation();
	const { id: eventId } = useParams<IdParamsType>();
	const isEditMode = customFieldId !== null;

	const formMethods = useForm<CustomFieldFormProps>({
		defaultValues,
		resolver: yupResolver(schema),
	});

	const { flash } = useAccordionForm(formMethods);
	const { isSubmitting } = formMethods.formState;

	// ── Data fetching ────────────────────────────────────────────────

	const { data: event } = useQuery({
		queryKey: ['event', eventId],
		queryFn: () => getData(`events/${eventId}`),
		enabled: !!eventId,
	});

	const { data: customField, isError: isCustomFieldError } = useQuery<CustomFieldType>({
		queryKey: ['custom-field', customFieldId],
		queryFn: () => getData(`/public/ticketing/custom-fields/${customFieldId}`),
		enabled: !!customFieldId,
	});

	// Seed the form once from the saved field (edit mode), held behind a spinner until then so the
	// radios don't paint defaults and re-select (the blink). Create mode renders immediately.
	const { isSeeded, seedGate } = useSeededEditForm({
		isEditMode,
		formMethods,
		seedData: customField,
		toFormValues: editFormValues,
		isError: isCustomFieldError,
	});

	// Default the question's scope to every ticket type ONLY when creating. In edit mode the saved
	// selection is seeded by editFormValues above and must not be overwritten when the event loads.
	useEffect(() => {
		if (event && !isEditMode) {
			formMethods.setValue('ticket_types_ids', event.ticket_types.map((ticket: any) => ticket.ID) ?? []);
		}
	}, [event, isEditMode]);

	// ── Mutations ────────────────────────────────────────────────────

	const invalidate = async () => {
		await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
		await queryClient.invalidateQueries({ queryKey: ['custom-fields', eventId] });
	};

	// `invalidate` is awaited inside onSuccess (before flashing) so react-query keeps the
	// mutation — and therefore react-hook-form's `isSubmitting` — pending until the list has
	// refetched. That keeps the save overlay (driven by isSubmitting → section.isSaving) on
	// screen for the whole save→refetch→close window, instead of vanishing the instant the
	// POST returns and leaving the form interactive while the refetch is still in flight.
	const { mutateAsync: createCustomField } = useMutation({
		mutationFn: (reqBody: CustomFieldFormProps) => post(`/events/${eventId}/custom-fields`, createRequestBody(reqBody)),
		onSuccess: async () => {
			await invalidate();
			flash('success', { title: t('customField-saved', 'Custom field saved'), onComplete: onDone });
		},
		onError: () => flash('error', { title: t('customField-save-failed', 'Failed to save custom field') }),
	});

	const { mutateAsync: updateCustomField } = useMutation({
		mutationFn: (reqBody: CustomFieldFormProps) => put(`/custom-fields/${customFieldId}`, createRequestBody(reqBody)),
		onSuccess: async () => {
			await invalidate();
			flash('success', { title: t('customField-saved', 'Custom field saved'), onComplete: onDone });
		},
		onError: () => flash('error', { title: t('customField-save-failed', 'Failed to save custom field') }),
	});

	const { mutate: deleteCustomField } = useMutation({
		mutationFn: () => deleteData(`/custom-fields/${customFieldId}`),
		onSuccess: () =>
			flash('success', {
				title: t('customField-deleted', 'Custom field deleted'),
				onComplete: async () => {
					await invalidate();
					onDone();
				},
			}),
		onError: () => flash('error', { title: t('customField-delete-failed', 'Failed to delete custom field') }),
	});

	// ── Submit ───────────────────────────────────────────────────────

	const handleSubmit = async (values: CustomFieldFormProps) => {
		try {
			if (isEditMode) await updateCustomField(values);
			else await createCustomField(values);
		} catch {
			// Error handled by mutation's onError
		}
	};

	// ── Render ───────────────────────────────────────────────────────

	if (!event) return null;

	// Edit mode is held behind the spinner until the saved field is seeded (create renders immediately).
	if (!isSeeded) return <>{seedGate}</>;

	return (
		<CustomFieldsForm formMethods={formMethods} onSubmit={handleSubmit} isPunchup>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
				<QuestionForm />
				<RightForm event={event} />
			</div>

			<div className={`entityEditActions ${isEditMode ? styles.actionsWithDelete : ''}`}>
				{isEditMode && (
					<PunchupButton type="button" variant="destructive" onClick={() => deleteCustomField()}>
						{t('button-delete')}
					</PunchupButton>
				)}
				<div style={{ display: 'flex', gap: '12px' }}>
					<PunchupButton text={t('button-cancel')} type="button" onClick={onDone} variant="secondary" />
					<PunchupButton
						text={isEditMode ? t('save') : t('add')}
						type="submit"
						disabled={isSubmitting || formMethods.watch('ticket_types_ids').length === 0}
						isLoading={isSubmitting}
					/>
				</div>
			</div>
		</CustomFieldsForm>
	);
};
